import styled, { css, keyframes } from 'styled-components';

import { mediaUp, rem } from 'approot/shared/style';
import {
  grey1,
  grey10,
  grey5,
  grey7,
  purple1,
  purple3,
  purple7,
} from 'approot/shared/styles/colours.style';
import {
  Heading30Responsive,
  PStyled,
  SmallStyled,
} from 'approot/shared/styles/typography.style';
import {
  borderRadius16,
  borderRadius8,
  boxShadow815Grey,
} from 'approot/shared/styles/borders.style';
import {
  spacing8,
  spacing12,
  spacing16,
  spacing24,
  spacing40,
  spacing32,
  spacing20,
} from 'approot/shared/styles/spacing.style';
import { flexCentreAlignCss } from 'approot/shared/styles/flex.style';
import { SecondaryButtonIcon } from 'approot/shared/styles/buttons/secondary-button.style';
import { BoxList } from 'approot/shared/design-system/box/box.style';
import { InteractiveDragContainer } from '../../shared/interactive-drag-drop/interactive-drag.style';

export const CANVAS_WIDTH = 1120;

export const popKeyframes = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

export const fadeInKeyframes = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

export const InteractiveResponseHeader = styled.div`
  margin-bottom: ${rem(spacing24)};
`;

export const InteractiveResponseTitle = styled(Heading30Responsive).attrs({
  $fontWeight: 'bold',
  textAlignCenter: true,
  marginBottom: 0,
})``;

export const InteractiveResponseSubtitle = styled(PStyled).attrs({
  colour: grey7,
  marginBottom: 0,
  textAlignCenter: true,
  responsive: true,
})``;

export const InteractiveCanvas = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  padding: ${rem(spacing20)} 0;

  ${mediaUp.md`
    padding: ${rem(spacing40)} 0;
  `}
`;

export const InteractiveResponseWrapper = styled.div`
  position: relative;
`;

export const InteractiveAnswerIcon = styled.div<{
  $isAnswered?: boolean;
  $animate?: boolean;
}>`
  opacity: ${({ $animate = true }) => ($animate ? 0 : 1)};
  visibility: ${({ $animate = true }) => ($animate ? 'hidden' : 'visible')};
  position: absolute;
  top: -${rem(spacing8)};
  right: -${rem(spacing8)};
  height: ${rem(40)};
  width: ${rem(40)};
  z-index: 4;

  ${({ $isAnswered, $animate = true }) =>
    $isAnswered &&
    $animate &&
    css`
      visibility: visible;
      animation:
        ${popKeyframes} 500ms ease 300ms forwards,
        ${fadeInKeyframes} 500ms ease 300ms forwards;
    `}
`;

export const InteractiveResponseSlideCount = styled(SmallStyled)`
  display: block;
  text-align: center;
`;

export const InteractiveResponseModalControlsWrapper = styled.div`
  ${flexCentreAlignCss}
  ${boxShadow815Grey}

  background-color: ${grey1};
  border-radius: ${rem(borderRadius16)};
  border: 1px solid ${grey5};
  padding: ${rem(spacing8)};
  position: fixed;
  left: ${rem(spacing32)};
  right: ${rem(spacing32)};
  bottom: ${rem(spacing8)};
`;

export const InteractiveResponseScrollableWrapper = styled.div`
  height: ${rem(224)};
  width: 100%;
  padding: ${rem(spacing12)} 0;
  overflow: hidden;
`;

export const InteractiveResponseScrollableList = styled(BoxList)`
  display: flex;
  flex-wrap: nowrap;
  gap: ${rem(spacing12)};
  height: 100%;
  transition: all 300ms ease;

  li {
    list-style: none;
  }

  ${InteractiveDragContainer} {
    width: ${rem(124)};
    height: ${rem(124)};
  }
`;

type InteractiveStackDragContainerProps = {
  $index: number;
  $isTransitioning?: boolean;
  $isDropped?: boolean;
};
export const InteractiveStackDragContainer = styled.li<InteractiveStackDragContainerProps>`
  height: ${rem(124)};
  width: ${rem(124)};
  transition: all 0.3s ease;

  ${({ $index }) =>
    `
    opacity: 1;
    transition-delay: ${50 * $index}ms;
  `}

  ${({ $isTransitioning }) =>
    $isTransitioning &&
    `
    opacity: 0;
  `}
`;

export const InteractiveStaticCanvas = styled.div`
  ${flexCentreAlignCss}

  background-color: ${grey1};
  border: 1px solid ${grey5};
  border-radius: ${rem(borderRadius8)};
  width: 100%;
  height: 100%;
`;

export const InteractiveStaticCanvasWrapper = styled.div`
  ${flexCentreAlignCss}

  width: ${rem(CANVAS_WIDTH)};
  padding: ${rem(spacing24)} 0;

  & > div {
    overflow: hidden;
    width: 100%;
  }
`;

type InteractiveCanvasDragZoneProps = {
  $isOver?: boolean;
  $isDropZoneClickable?: boolean;
  $isHighlighted?: boolean;
};
export const InteractiveCanvasDragZone = styled(
  BoxList
)<InteractiveCanvasDragZoneProps>`
  ${flexCentreAlignCss}
  flex-wrap: wrap;
  gap: ${rem(spacing8)};
  padding: ${rem(spacing8)};
  min-height: ${rem(140)};
  position: relative;
  border: 1px solid ${grey5};
  border-radius: ${rem(borderRadius8)};

  ${({ $isOver }) =>
    $isOver &&
    css`
      background-color: ${purple3};
      border: 1px solid ${grey10};
    `}

  ${InteractiveDragContainer}, ${InteractiveStackDragContainer} {
    width: ${rem(124)};
    height: ${rem(124)};
  }

  ${({ $isDropZoneClickable }) =>
    $isDropZoneClickable &&
    css`
      background-color: ${purple1};
      border: 1px solid ${purple7};
    `}

  ${({ $isHighlighted }) =>
    $isHighlighted &&
    `
    background-color: ${purple1};
    border-radius: ${rem(borderRadius8)};
  `}
`;

export const InteractiveCanvasDragZoneOuter = styled.div`
  ${flexCentreAlignCss}
  margin: 0 ${rem(spacing24)} ${rem(spacing24)};
`;

export const InteractiveResponsePreviewContainer = styled(BoxList)`
  ${flexCentreAlignCss}

  flex-wrap: wrap;
  gap: ${rem(spacing8)};
  margin: auto;
  min-height: ${rem(140)};
  padding: ${rem(spacing24)} 0 0 0;
  position: relative;
  margin-bottom: ${rem(spacing24)};
  width: 100%;
`;

export const InteractiveResponseSmallButton = styled(SecondaryButtonIcon)`
  width: ${rem(140)};
`;

export const InteractiveResponseSubActivityWrapper = styled.div`
  padding: ${rem(spacing16)};
  ${mediaUp.md`
    padding-top: 0;
    padding-left: 0;
    padding-right: 0;
  `}
`;

export const InteractiveResponseSubActivity = styled.div`
  background-color: ${grey1};
  border: 1px solid ${grey5};
  border-radius: ${rem(borderRadius8)};
  padding: ${rem(spacing12)} ${rem(spacing16)} 0 ${rem(spacing16)};
`;

export const InteractiveResponseMobileWrapper = styled.div`
  padding: ${rem(spacing16)};
`;

export const InteractiveResponsePlayContainer = styled.div`
  background-color: ${grey1};
  border-radius: ${rem(borderRadius8)};
  border: 1px solid ${grey5};
`;
